import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UiModule } from '@components/ui/ui-module';
import { CardSet, RemoteCard, RemoteSet } from '@models/card.model';
import { LogList } from '@models/common.model';
import { Scrap } from '@services/scrap.service';
import { switchMap, tap, from, concatMap, catchError, of, EMPTY } from 'rxjs';

@Component({
  selector: 'lgb-set-scraping',
  imports: [
    UiModule,
    DatePipe,
    CommonModule,
    FormsModule
  ],
  templateUrl: './set-scraping.html',
  styleUrl: './set-scraping.css',
})
export class SetScraping {
  //service
  readonly scrap=inject(Scrap);
  readonly route=inject(ActivatedRoute);

  //data
  readonly remote=signal<RemoteSet>(null);
  readonly cards=signal<RemoteCard[]>([]);
  readonly setCard=signal<CardSet>(null);
  readonly canImport=signal(true);
  readonly errors=new LogList();

  //ui
  readonly loading=signal<boolean>(false);
  readonly importing=signal<boolean>(false);
  readonly tabActive=signal('logs');
  readonly logs=new LogList();
  readonly cardLoading=signal<RemoteCard>(null);
  readonly valLoad=signal<number>(0);
  readonly maxLoad=computed(() => {
    let max: number=0;
    if(this.importing())
      max=((this.cards().length)? this.cards().length+10 : 100);
    return max;
  });
  readonly nbImported=signal<number>(0);
  readonly isError=signal(false);           // sert à indiqué si on affiche uniquement les carte en erreurs
  readonly cardList=computed(() => ((this.isError())? this.cards().filter(c => c.stateImport=='Erreur') : this.cards()));      // la liste des cartes à affiché déffère en fonction du filtre "erreur"

  constructor(){
    this.route.paramMap
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(params => this.scrap.getremoteSet(params.get('id')))
      )
      .subscribe(remote => {
        this.remote.set(remote);
        this.logs.add('Prêt à importer...');
        this.errors.add("Aucune erreur.");
        this.loading.set(false);
      });
  }

  initImport(){
    this.importing.set(true);
    this.canImport.set(true);
    this.nbImported.set(0);
    this.logs.empty();
    this.errors.empty();
    this.errors.add("Aucune erreur.");
  }

  importSet(){
    this.initImport();
    this.logs.add(`Lancement de l'import du set ${this.remote().name}...`);
    this.valLoad.set(5);
    this.scrap.importSet(this.remote().id)
      .pipe(
        switchMap(response => {
          this.cards.set(response.cards);
          this.logs.addWrapLogs(response.logs);
          this.setCard.set(response.set);
          this.valLoad.set(10);
          return from(this.cards());
        }),
        concatMap(card => {
          this.cardLoading.set(card);
          if(this.canImport())
            return this.getObs$(card);
          else
            return EMPTY;
        })
      )
      .subscribe(this.getSubscribe());
  }

  private getObs$(card: RemoteCard){
    return this.importCard(this.setCard().ident, card.id).pipe(
      tap(() => card.stateImport='Importée'),
      catchError(error => {
        card.stateImport='Erreur';
        this.logs.add(`Erreur lors de l'import de la carte ${this.cardLoading().id}`, 'error');
        this.logs.add(error, 'error');
        this.errors.add(`Erreur lors de l'import de la carte ${this.cardLoading().id}`, 'error');
        this.errors.add(error, 'error');
        return of(null);
      })
    );
  }

  private getSubscribe(){
    return {
      next: (response: any) => {
        if(response){
          //ajout du log 
          this.logs.addWrapLogs(response.logs);
          this.errors
          this.nbImported.update(val => val+=1);
        }
        this.valLoad.update(val => val+=1);
      },
      complete: () => {
        this.logs.add(`Import du set terminé`, 'success');
        this.logs.add(`Nombre de cartes importées ${this.nbImported()}/${this.cards().length}`, 'success')
        this.importing.set(false);
      }
    }
  }

  importCard(setId: string, cardId: string){
    return this.scrap.importCard(setId, cardId);
  }

  getClassState(state: string){
    return {
      'badge-error': state=='Erreur',
      'badge-success': state=='Importée'
    };
  }

  getRemoteImg(url: string){
    if(url)
      return [`${url}/low.jpg`];
    else
      return [];
  }

  stopImport(){
    this.canImport.set(false);
    this.importing.set(false);
  }

  replay(card: RemoteCard){
    this.initImport();
    card.stateImport=null;
    this.logs.add(`Import de la carte : ${card.id} - ${card.name}`);
    this.valLoad.set(1);
    this.valLoad.set(3);
    this.getObs$(card).subscribe(this.getSubscribe());
  }

  replayLoop(card: RemoteCard){
    const cards: RemoteCard[]=this.cards().slice(this.cards().findIndex(c => c.id==card.id));
    cards.map(c => {
      c.stateImport=null;
      return c;
    });
    this.initImport();
    this.logs.add(`Relance de l'import à partir de la carte : ${card.id} - ${card.name}`);
    this.valLoad.set(1);
    this.valLoad.set(cards.length);
    from(cards)
      .pipe(
        concatMap(card => {
          this.cardLoading.set(card);
          if(this.canImport())
            return this.getObs$(card);
          else
            return EMPTY;
        })
      )
      .subscribe(this.getSubscribe());
  }
}
